import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  CircularProgress,
  TextField,
  Typography,
  FormHelperText,
  MenuItem,
  Avatar,
  Paper,
  Tooltip,
} from "@material-ui/core";
import { fade, useTheme } from "@material-ui/core/styles";
import Alert from "@material-ui/lab/Alert";
import { Autocomplete } from "@material-ui/lab";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import Form from "@rjsf/material-ui";
import Authorizer from "../Authorizer";
import { fetchAllApps, selectAllTriggers, selectApps } from "../../features/allApps/allAppsSlice";
import { createDefaultTrigger } from "../../features/agents/types";
import { findCatalogAppMeta, getTriggerSetupIssue } from "../../features/agents/agentSetupIssues";
import { useAgentDynamicGetDD } from "../../features/agents/useAgentDynamicGetDD";
import { dedupeAgentTriggers } from "../../features/agents/draftMapper";

const parsePerformSpec = (perform = "") => {
  const split = String(perform || "").split(".");
  if (split.length < 6) return null;
  const [app, fieldTitle, action, performDataHead, performKey, performValue] = split;
  return { app, fieldTitle, action, performDataHead, performKey, performValue };
};

const parseDynamicSpec = (dynamic = "") => {
  const split = String(dynamic || "").split(".");
  if (split.length < 6) return null;
  const [app, fieldTitle, action, dataHead, key, value] = split;
  return { app, fieldTitle, action, dataHead, key, value };
};

const extractPrimitive = (value) => {
  let current = value;
  while (
    current &&
    typeof current === "object" &&
    !Array.isArray(current) &&
    Object.prototype.hasOwnProperty.call(current, "value")
  ) {
    current = current.value;
  }
  return current;
};

const TriggerFieldsModal = ({
  open,
  appMeta,
  fields,
  credentialId,
  authName,
  onCredentialChange,
  onAuthNameChange,
  user,
  tenant,
  onClose,
  onSave,
  onReplaceTrigger,
}) => {
  const [localFields, setLocalFields] = useState([]);

  useEffect(() => {
    setLocalFields(Array.isArray(fields) ? JSON.parse(JSON.stringify(fields)) : []);
  }, [fields]);

  const { dynamicOptionsByField, dynamicLoadingByField, loadingDynamic } = useAgentDynamicGetDD({
    open,
    appMeta,
    credentialId,
    localFields,
    parseDynamicSpec,
    parsePerformSpec,
  });

  const updateFieldValue = (index, formData) => {
    setLocalFields((prev) => prev.map((field, idx) => (idx === index ? { ...field, value: formData } : field)));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Configure trigger fields</DialogTitle>
      <DialogContent dividers>
        {appMeta?.auth ? (
          <Box mb={2}>
            <Authorizer
              breakpoint="large"
              user={user}
              tenant={tenant}
              node={{ id: "agent-trigger-config", data: { authname: authName || "", credential: credentialId || "" } }}
              app={appMeta.app}
              applabel={appMeta.label}
              icon={appMeta.icon}
              sendData={(id) => onCredentialChange?.(id)}
              sendAuth={(name) => onAuthNameChange?.(name)}
              saveNode={() => {}}
              value={authName || ""}
              sendLoggedOutToAuthorizer={() => {}}
            />
          </Box>
        ) : null}
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
          Set trigger options for this event. These fields mirror the same trigger schema used in workflow trigger setup.
        </Typography>
        {loadingDynamic ? (
          <Box display="flex" alignItems="center" style={{ marginBottom: 12 }}>
            <CircularProgress size={18} style={{ marginRight: 8 }} />
            <Typography variant="body2" color="textSecondary">
              Loading dynamic options...
            </Typography>
          </Box>
        ) : null}
        {(localFields || []).filter((field) => field?.hidden !== true && field?.schema?.hidden !== true).length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            This trigger event has no additional fields to configure.
          </Typography>
        ) : (
          (localFields || []).map((field, index) => {
            if (field?.hidden === true || field?.schema?.hidden === true) {
              return null;
            }
            const schema = {
              ...(field.schema || { type: "string" }),
              title: field.title || field.name || `Field ${index + 1}`,
            };
            const dynamicOptions = dynamicOptionsByField[field?.name] || [];
            const dynamicLoading = Boolean(dynamicLoadingByField[field?.name]);
            const isDynamic = Boolean(schema.dynamic);
            const currentValue = field?.value;
            const selectedDynamicValue =
              currentValue && typeof currentValue === "object"
                ? currentValue?.value ?? currentValue?.id ?? ""
                : currentValue ?? "";

            return (
              <Box key={field.name || index} mb={2}>
                {isDynamic ? (
                  <TextField
                    select
                    fullWidth
                    variant="outlined"
                    label={schema.title}
                    value={selectedDynamicValue}
                    onChange={(event) => {
                      const selected = dynamicOptions.find((item) => String(item.id) === String(event.target.value));
                      updateFieldValue(index, selected ? { label: selected.name, value: selected.id } : event.target.value);
                    }}
                    SelectProps={{ native: false }}
                    helperText={dynamicLoading ? "Loading options..." : schema.description || ""}
                    disabled={dynamicLoading || !credentialId}
                  >
                    {dynamicOptions.map((option) => (
                      <MenuItem key={`${field.name}_${option.id}`} value={option.id}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <Form
                    schema={schema}
                    formData={field.value}
                    onChange={(event) => updateFieldValue(index, event.formData)}
                    liveValidate={false}
                    noHtml5Validate
                  >
                    <div />
                  </Form>
                )}
              </Box>
            );
          })
        )}
      </DialogContent>
      <DialogActions>
        {onReplaceTrigger ? (
          <Button onClick={onReplaceTrigger} color="secondary" style={{ marginRight: "auto" }}>
            Replace trigger
          </Button>
        ) : null}
        <Button onClick={onClose}>Cancel</Button>
        <Button color="primary" variant="contained" onClick={() => onSave(localFields)}>
          Save trigger fields
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const AgentWatchesSection = ({ triggers = [], onChange, onCommit = null, user = null, tenant = null }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const triggerApps = useSelector(selectAllTriggers);
  const allAppsCatalog = useSelector(selectApps);
  const appsLoading = useSelector((state) => state.allApps?.loading);
  const triggerSectionRef = useRef(null);
  const [setupGuideAlert, setSetupGuideAlert] = useState(null);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [fieldsModalOpen, setFieldsModalOpen] = useState(false);
  const [setupModalOpen, setSetupModalOpen] = useState(false);
  const [setupDraft, setSetupDraft] = useState(createDefaultTrigger());
  const [replacementMode, setReplacementMode] = useState(false);
  const [replacementDraft, setReplacementDraft] = useState(null);

  const singleTrigger = useMemo(() => {
    const healed = dedupeAgentTriggers(triggers);
    if (healed.length > 0) return healed[0];
    return createDefaultTrigger();
  }, [triggers]);
  const triggerForFields = replacementMode ? (replacementDraft || createDefaultTrigger()) : singleTrigger;

  const setSingleTrigger = (patch) => {
    const next = { ...singleTrigger, ...patch };
    onChange(dedupeAgentTriggers([next]));
  };

  // Heal stacked triggers from older copilot merges (UI only ever edits the primary).
  useEffect(() => {
    if (!Array.isArray(triggers) || triggers.length <= 1) return;
    const healed = dedupeAgentTriggers(triggers);
    if (healed.length < triggers.length) onChange(healed);
    // Intentionally omit onChange — parent passes an inline updater.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggers]);

  useEffect(() => {
    if (!allAppsCatalog || allAppsCatalog.length === 0) {
      dispatch(fetchAllApps(1));
    }
  }, [dispatch, allAppsCatalog]);

  const setupSelectedApp = useMemo(
    () => findCatalogAppMeta(allAppsCatalog || [], setupDraft.source),
    [allAppsCatalog, setupDraft.source]
  );

  useEffect(() => {
    let cancelled = false;
    const loadEvents = async () => {
      if (!setupSelectedApp?.formDataEndpoint) {
        setEvents([]);
        return;
      }
      setEventsLoading(true);
      try {
        const response = await axios.get(setupSelectedApp.formDataEndpoint);
        const triggerMap = response?.data?.triggers || {};
        const mapped = Object.keys(triggerMap).map((key) => ({
          key,
          label: triggerMap[key]?.label || key,
          ...triggerMap[key],
        }));
        if (!cancelled) setEvents(mapped);
      } catch (err) {
        if (!cancelled) setEvents([]);
      } finally {
        if (!cancelled) setEventsLoading(false);
      }
    };
    loadEvents();
    return () => {
      cancelled = true;
    };
  }, [setupSelectedApp]);

  const setupSelectedEvent = useMemo(
    () => (events || []).find((item) => item.key === setupDraft.type) || null,
    [events, setupDraft.type]
  );
  const isConfigured = Boolean(singleTrigger.source && singleTrigger.type);

  const triggerAppMeta = useMemo(
    () => findCatalogAppMeta(allAppsCatalog || [], singleTrigger.source),
    [allAppsCatalog, singleTrigger.source]
  );

  const triggerAppMetaForModal = useMemo(
    () => findCatalogAppMeta(allAppsCatalog || [], triggerForFields.source),
    [allAppsCatalog, triggerForFields.source]
  );

  const triggerSetupIssue = useMemo(
    () => getTriggerSetupIssue(singleTrigger, triggerAppMeta),
    [singleTrigger, triggerAppMeta]
  );

  const openAddTrigger = () => {
    setReplacementMode(false);
    setReplacementDraft(null);
    setSetupDraft({
      ...createDefaultTrigger(),
      source: "",
      type: "",
      trigger_key: "",
      trigger_label: "",
      app_label: "",
      app_icon: "",
      url: "",
      fields: [],
      credential: "",
      authname: "",
      enabled: true,
    });
    setEvents([]);
    setSetupModalOpen(true);
  };

  const openEditTrigger = (issue = null) => {
    if (issue) {
      setSetupGuideAlert({ title: issue.alertTitle, body: issue.alertBody });
      triggerSectionRef.current?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
    } else {
      setSetupGuideAlert(null);
    }
    setFieldsModalOpen(true);
  };

  const beginReplaceFlow = () => {
    setFieldsModalOpen(false);
    setReplacementMode(true);
    const fresh = {
      ...createDefaultTrigger(),
      source: "",
      type: "",
      trigger_key: "",
      trigger_label: "",
      app_label: "",
      app_icon: "",
      url: "",
      fields: [],
      credential: "",
      authname: "",
      enabled: true,
    };
    setReplacementDraft(fresh);
    setSetupDraft(fresh);
    setEvents([]);
    setSetupModalOpen(true);
  };

  const abortReplaceFlow = () => {
    setReplacementMode(false);
    setReplacementDraft(null);
    setSetupModalOpen(false);
    setFieldsModalOpen(false);
  };

  const handleSetupEventChange = (eventObj) => {
    if (!eventObj) {
      setSetupDraft((prev) => ({
        ...prev,
        type: "",
        trigger_key: "",
        trigger_label: "",
        event_type: "",
        trigger_action: "",
        performList: "",
        triggerResource: "",
        pollingParams: {},
        fields: [],
      }));
      return;
    }
    const currentFieldsByName = new Map((setupDraft.fields || []).map((field) => [field?.name, field]));
    const mergedFields = (eventObj.fields || []).map((field) => {
      const existing = currentFieldsByName.get(field?.name);
      return existing ? { ...field, value: existing.value } : field;
    });
    const eventType = eventObj.type || "";
    const listAction = eventObj.action || eventObj.performList || "";
    const performList = eventType === "polling" ? listAction : eventObj.performList || "";
    setSetupDraft((prev) => ({
      ...prev,
      type: eventObj.key,
      trigger_key: eventObj.key,
      trigger_label: eventObj.label || eventObj.key,
      event_type: eventType,
      trigger_action: eventObj.action || "",
      performList,
      triggerResource: eventType === "polling" ? listAction : eventObj.action || "",
      pollingParams: eventObj.pollingParams || {},
      fields: mergedFields,
    }));
  };

  const saveSetupAndConfigure = () => {
    if (!setupDraft.source || !setupDraft.type) return;
    if (replacementMode) {
      setReplacementDraft({ ...setupDraft });
    } else {
      onChange([{ ...setupDraft }]);
    }
    setSetupModalOpen(false);
    setFieldsModalOpen(true);
  };

  return (
    <Card ref={triggerSectionRef} id="agent-trigger-section">
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6">Trigger</Typography>
        </Box>
        {setupGuideAlert ? (
          <Box mb={2}>
            <Alert severity="error" onClose={() => setSetupGuideAlert(null)}>
              <Typography variant="subtitle2" gutterBottom>
                {setupGuideAlert.title}
              </Typography>
              <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>
                {setupGuideAlert.body}
              </Typography>
            </Alert>
          </Box>
        ) : null}
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
          Select one trigger app and event to start this agent.
        </Typography>
        {isConfigured ? (
          <Paper
            variant="outlined"
            onClick={() => {
              if (triggerSetupIssue) openEditTrigger(triggerSetupIssue);
            }}
            style={{
              padding: 12,
              cursor: triggerSetupIssue ? "pointer" : "default",
              backgroundColor: triggerSetupIssue ? fade(theme.palette.error.main, 0.08) : undefined,
              borderColor: triggerSetupIssue ? theme.palette.error.main : undefined,
              borderWidth: triggerSetupIssue ? 1 : undefined,
            }}
          >
            <Box display="flex" alignItems="center" flexWrap="nowrap" style={{ gap: 8, minWidth: 0 }}>
              <Box display="flex" alignItems="center" minWidth={0} flex={1} style={{ minWidth: 0 }}>
                {singleTrigger.app_icon ? (
                  <Avatar src={singleTrigger.app_icon} style={{ marginRight: 8, flexShrink: 0 }} />
                ) : null}
                <Typography variant="body1" noWrap style={{ minWidth: 0 }}>
                  {singleTrigger.app_label || singleTrigger.source}: {singleTrigger.trigger_label || singleTrigger.type}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" style={{ gap: 8, flexShrink: 0, marginLeft: "auto" }}>
                {triggerSetupIssue ? (
                  <Tooltip title={<span style={{ whiteSpace: "pre-line" }}>{triggerSetupIssue.tooltip}</span>}>
                    <Chip
                      size="small"
                      label="Setup issue"
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        backgroundColor: fade(theme.palette.error.main, 0.18),
                        color: theme.palette.error.dark,
                        fontWeight: 600,
                      }}
                    />
                  </Tooltip>
                ) : null}
                <Button
                  size="small"
                  variant="outlined"
                  onClick={(e) => {
                    e.stopPropagation();
                    openEditTrigger(null);
                  }}
                >
                  Edit trigger
                </Button>
              </Box>
            </Box>
          </Paper>
        ) : (
          <Paper variant="outlined" style={{ padding: 12 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="body1">+ Add a trigger to start the agent...</Typography>
              <Button size="small" variant="outlined" onClick={openAddTrigger}>
                + Add trigger
              </Button>
            </Box>
          </Paper>
        )}

        <Dialog open={setupModalOpen} onClose={() => setSetupModalOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>{isConfigured ? "Edit trigger" : "Add trigger"}</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Autocomplete
                  options={triggerApps || []}
                  loading={appsLoading === "loading"}
                  value={setupSelectedApp}
                  getOptionLabel={(option) => option?.name || ""}
                  renderOption={(option) => (
                    <Box display="flex" alignItems="center">
                      <Avatar src={option?.icon} style={{ width: 22, height: 22, marginRight: 8 }} />
                      <Typography variant="body2">{option?.name || ""}</Typography>
                    </Box>
                  )}
                  onChange={(_, value) => {
                    if (!value) {
                      setSetupDraft((prev) => ({
                        ...prev,
                        source: "",
                        type: "",
                        trigger_key: "",
                        trigger_label: "",
                        app_label: "",
                        app_icon: "",
                        url: "",
                        fields: [],
                        credential: "",
                        authname: "",
                        enabled: true,
                      }));
                      return;
                    }
                    setSetupDraft((prev) => ({
                      ...prev,
                      source: value.app,
                      type: "",
                      trigger_key: "",
                      trigger_label: "",
                      app_label: value.name,
                      app_icon: value.icon,
                      url: value.formDataEndpoint,
                      fields: [],
                      credential: prev.source === value.app ? prev.credential : "",
                      authname: prev.source === value.app ? prev.authname : "",
                      enabled: true,
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Trigger app"
                      variant="outlined"
                      placeholder="Search trigger apps..."
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: setupSelectedApp?.icon ? (
                          <>
                            <Avatar src={setupSelectedApp.icon} style={{ width: 22, height: 22, marginRight: 8 }} />
                            {params.InputProps.startAdornment}
                          </>
                        ) : (
                          params.InputProps.startAdornment
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              {setupSelectedApp ? (
                <Grid item xs={12}>
                  <Autocomplete
                    options={events || []}
                    loading={eventsLoading}
                    value={setupSelectedEvent}
                    getOptionLabel={(option) => option?.label || option?.key || ""}
                    onChange={(_, value) => handleSetupEventChange(value)}
                    renderOption={(option) => (
                      <Box display="flex" alignItems="flex-start">
                        {setupSelectedApp?.icon ? (
                          <Avatar src={setupSelectedApp.icon} style={{ width: 22, height: 22, marginRight: 8, marginTop: 2 }} />
                        ) : null}
                        <Box>
                          <Typography variant="body1" style={{ fontWeight: 700 }}>
                            {option?.label || option?.key || ""}
                          </Typography>
                          {option?.description ? (
                            <Typography variant="body2" color="textSecondary">
                              {option.description}
                            </Typography>
                          ) : null}
                        </Box>
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Trigger event"
                        variant="outlined"
                        placeholder="Select an event..."
                        helperText={setupSelectedApp?.name ? `Search for ${setupSelectedApp.name} triggers` : ""}
                      />
                    )}
                  />
                </Grid>
              ) : null}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                if (replacementMode) {
                  abortReplaceFlow();
                } else {
                  setSetupModalOpen(false);
                }
              }}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              variant="contained"
              disabled={!setupDraft.source || !setupDraft.type}
              onClick={saveSetupAndConfigure}
            >
              {setupDraft.fields && setupDraft.fields.length > 0 ? "Next: configure fields" : "Save trigger"}
            </Button>
          </DialogActions>
        </Dialog>
        <TriggerFieldsModal
          open={fieldsModalOpen}
          appMeta={
            triggerAppMetaForModal
              ? {
                  url: triggerAppMetaForModal.formDataEndpoint,
                  app: triggerAppMetaForModal.app,
                  label: triggerAppMetaForModal.name,
                  icon: triggerAppMetaForModal.icon,
                  auth: triggerAppMetaForModal.auth === true,
                }
              : null
          }
          fields={triggerForFields.fields || []}
          credentialId={triggerForFields.credential || null}
          authName={triggerForFields.authname || ""}
          onCredentialChange={(credential) => {
            if (replacementMode) {
              setReplacementDraft((prev) => ({ ...(prev || createDefaultTrigger()), credential }));
              return;
            }
            setSingleTrigger({ credential });
          }}
          onAuthNameChange={(authname) => {
            if (replacementMode) {
              setReplacementDraft((prev) => ({ ...(prev || createDefaultTrigger()), authname }));
              return;
            }
            setSingleTrigger({ authname });
          }}
          user={user || localStorage.getItem("user")}
          tenant={tenant || localStorage.getItem("tenant")}
          onClose={() => {
            setSetupGuideAlert(null);
            if (replacementMode) {
              abortReplaceFlow();
            } else {
              setFieldsModalOpen(false);
            }
          }}
          onReplaceTrigger={isConfigured && !replacementMode ? beginReplaceFlow : null}
          onSave={(nextFields) => {
            setSetupGuideAlert(null);
            if (replacementMode) {
              const candidate = { ...(replacementDraft || createDefaultTrigger()), fields: nextFields || [] };
              if (candidate.source && candidate.type) {
                const nextTriggers = dedupeAgentTriggers([candidate]);
                onChange(nextTriggers);
                if (typeof onCommit === "function") onCommit(nextTriggers);
              }
              setReplacementMode(false);
              setReplacementDraft(null);
              setFieldsModalOpen(false);
              return;
            }
            const nextTriggers = dedupeAgentTriggers([{ ...singleTrigger, fields: nextFields || [] }]);
            onChange(nextTriggers);
            setFieldsModalOpen(false);
            if (typeof onCommit === "function") onCommit(nextTriggers);
          }}
        />
      </CardContent>
    </Card>
  );
};

export default AgentWatchesSection;
